import React, { useState, useCallback } from 'react';
import html2canvas from 'html2canvas';
import { 
  MessageCircle, X, Send, Loader2, Bug, Lightbulb, 
  HelpCircle, Camera, CheckCircle, Image as ImageIcon
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import api from '../services/api';

const CATEGORIES = [
  { value: 'general', label: 'General Support', icon: MessageCircle, color: 'blue' },
  { value: 'bug', label: 'Report a Bug', icon: Bug, color: 'red' },
  { value: 'feature', label: 'Feature Request', icon: Lightbulb, color: 'amber' },
  { value: 'question', label: 'Question', icon: HelpCircle, color: 'purple' },
];

const PRIORITIES = [
  { value: 'low', label: 'Low', color: 'gray' },
  { value: 'medium', label: 'Medium', color: 'blue' },
  { value: 'high', label: 'High', color: 'orange' },
  { value: 'urgent', label: 'Urgent', color: 'red' },
];

export function SupportButton() {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [screenshot, setScreenshot] = useState(null);
  const [capturing, setCapturing] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'general',
    priority: 'medium',
  });

  const captureScreenshot = useCallback(async () => {
    setCapturing(true);
    setIsOpen(false);
    // Wait for the panel to close before capturing
    await new Promise(r => setTimeout(r, 350));
    try {
      const canvas = await html2canvas(document.body, {
        scale: 0.5,
        useCORS: true,
        logging: false,
        ignoreElements: (el) =>
          el.id === 'support-widget' || el.id === 'feedback-widget',
      });
      const dataUrl = canvas.toDataURL('image/jpeg', 0.6);
      setScreenshot(dataUrl);
      toast.success('Screenshot captured');
    } catch {
      toast.error('Screenshot capture failed');
    } finally {
      setCapturing(false);
      setIsOpen(true);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error('Please enter a title');
      return;
    }
    
    if (!formData.description.trim()) {
      toast.error('Please describe your issue or request');
      return;
    }

    setIsSubmitting(true);
    try {
      await api.post('/feedback/support', {
        ...formData,
        page_url: window.location.href,
        screenshot,
      });
      
      setIsSubmitted(true);
      toast.success('Support request submitted!');
      
      setTimeout(() => {
        setIsOpen(false);
        setIsSubmitted(false);
        setScreenshot(null);
        setFormData({
          title: '',
          description: '',
          category: 'general',
          priority: 'medium',
        });
      }, 2000);
    } catch (error) {
      console.error('Error submitting support request:', error);
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div id="support-widget">
      {/* Floating Support Button */}
      <Button
        onClick={() => setIsOpen(true)}
        variant="outline"
        size="icon"
        className="fixed bottom-6 right-36 h-12 w-12 rounded-full shadow-lg bg-gradient-to-br from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 border-0 text-white z-50"
        data-testid="support-button"
      >
        <MessageCircle className="h-5 w-5" />
      </Button>

      {/* Support Request Panel */}
      {isOpen && (
        <div className="fixed bottom-20 right-36 w-96 bg-white rounded-xl shadow-2xl border z-50 overflow-hidden animate-in slide-in-from-bottom-4 duration-200">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-4 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MessageCircle className="w-5 h-5" />
                <h3 className="font-semibold">Support Request</h3>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsOpen(false)}
                className="h-8 w-8 text-white hover:bg-white/20"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-sm text-white/80 mt-1">
              We're here to help! Describe your issue below.
            </p>
          </div>

          {/* Content */}
          {isSubmitted ? (
            <div className="p-8 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h4 className="font-semibold text-gray-900 mb-2">Request Submitted!</h4>
              <p className="text-sm text-gray-500">
                Our team will review your request and get back to you soon.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="p-4 space-y-3">
              {/* Category Selection */}
              <div className="grid grid-cols-2 gap-2">
                {CATEGORIES.map(cat => {
                  const Icon = cat.icon;
                  const isSelected = formData.category === cat.value;
                  return (
                    <button
                      key={cat.value}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, category: cat.value }))}
                      className={`p-2 rounded-lg border text-left transition-all ${
                        isSelected 
                          ? `border-${cat.color}-500 bg-${cat.color}-50 ring-1 ring-${cat.color}-500` 
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <Icon className={`w-4 h-4 ${isSelected ? `text-${cat.color}-600` : 'text-gray-400'}`} />
                        <span className={`text-xs font-medium ${isSelected ? `text-${cat.color}-700` : 'text-gray-600'}`}>
                          {cat.label}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Title */}
              <div className="space-y-1.5">
                <Label htmlFor="title" className="text-sm">Title *</Label>
                <Input
                  id="title"
                  placeholder="Brief summary of your request"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  className="text-sm"
                  data-testid="support-title"
                />
              </div>

              {/* Description */}
              <div className="space-y-1.5">
                <Label htmlFor="description" className="text-sm">Description *</Label>
                <Textarea
                  id="description"
                  placeholder="Please provide details about your issue or request..."
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="text-sm min-h-[80px] resize-none"
                  data-testid="support-description"
                />
              </div>

              {/* Priority */}
              <div className="space-y-1.5">
                <Label className="text-sm">Priority</Label>
                <Select 
                  value={formData.priority} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, priority: value }))}
                >
                  <SelectTrigger className="text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map(p => (
                      <SelectItem key={p.value} value={p.value}>
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full bg-${p.color}-500`} />
                          {p.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Screenshot Capture */}
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs gap-1.5"
                  onClick={captureScreenshot}
                  disabled={capturing}
                  data-testid="support-screenshot-btn"
                >
                  {capturing ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <Camera className="w-3.5 h-3.5" />
                  )}
                  {screenshot ? 'Retake Screenshot' : 'Attach Screenshot'}
                </Button>
                {screenshot && (
                  <div className="relative group flex items-center gap-1.5">
                    <img
                      src={screenshot}
                      alt="Screenshot preview"
                      className="h-8 w-14 object-cover rounded border"
                    />
                    <button
                      type="button"
                      onClick={() => setScreenshot(null)}
                      className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-[9px] opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-2.5 h-2.5" />
                    </button>
                    <span className="text-xs text-green-600 font-medium">Attached</span>
                  </div>
                )}
              </div>

              {/* Submit Button */}
              <Button 
                type="submit" 
                className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700"
                disabled={isSubmitting}
                data-testid="support-submit"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Submit Request
                  </>
                )}
              </Button>
            </form>
          )}
        </div>
      )}
    </div>
  );
}

export default SupportButton;
